import { cloneDeep } from '@apollo/client/utilities';
import { ClassConstructor } from 'class-transformer';
import { EntityAuthorization } from './entityAuthorization';

export type IdArray = [{ id: string }];

export type Entity = CompanyEntity | StockLocationEntity;

export enum ExternalEntityType {
  bc_job = 'bc_job',
  bc_job_line = 'bc_job_line',
  bc_purchase_order = 'bc_purchase_order',
  bc_purchase_order_line = 'bc_purchase_order_line',
  bc_item_journal = 'bc_item_journal',
  bc_item_journal_line = 'bc_item_journal_line',
  bc_location = 'bc_location',
  bc_material = 'bc_material',

  sap_material = 'sap_material',
  sap_batch = 'sap_batch',
  sap_inbound_delivery = 'sap_inbound_delivery',
  sap_outbound_delivery = 'sap_outbound_delivery',

  elteros_custom_invoice = 'elteros_custom_invoice',
}

export enum ExternalSourceType {
  sap = 'sap',
  businessCentral = 'businessCentral',
}

export class ExternalEntityIdentifier {
  settingsId: string;
  externalId: string;
  type: ExternalEntityType;
  source: ExternalSourceType;

  constructor(settingsId: string, externalId: string, type: ExternalEntityType, source: ExternalSourceType) {
    this.settingsId = settingsId;
    this.externalId = externalId;
    this.type = type;
    this.source = source;
  }
}

export interface EntityInput<T> {
  forCreate: () => CreateEntity<T>;
  forDelete: () => DeleteEntity<T>;
  forUpdate: () => UpdateEntity<T>;

  // validate(): boolean;
}

export abstract class CompanyEntity {
  id!: string;
  companyId: string;
  version!: number;
  createdAt!: Date;
  updatedAt!: Date;
  createdBy!: string;
  updatedBy!: string;
  public externalIdentifier?: ExternalEntityIdentifier;

  authorization?: EntityAuthorization<any>;

  constructor(companyId: string) {
    this.companyId = companyId;
  }

  protected validateEntity<T, K>(fields: (keyof T)[], fn: (field: keyof T) => K | null): Map<keyof T, K> {
    const uniqueFields = new Set(fields);
    const map = new Map();

    for (const field of uniqueFields) {
      const item = fn(field);
      if (item) map.set(field, item);
    }

    return map;
  }
}

export abstract class StockLocationEntity extends CompanyEntity {
  stockLocationId!: string;

  constructor(companyId: string, stockLocationId: string) {
    super(companyId);
    this.stockLocationId = stockLocationId;
  }

  withStockLocationId(stockLocationId?: string) {
    (this.stockLocationId as any) = stockLocationId;
    return cloneDeep(this);
  }
}

interface ClassType<T = any> {
  new (...args: any[]): T;
}

const ENTITY_FIELDS = ['createdBy', 'createdAt', 'updatedBy', 'updatedAt'];

interface CreateEntity<T> {}
export function forCreate<T extends ClassType>(BaseClass: T) {
  return class CreateTrait extends BaseClass implements CreateEntity<T> {
    constructor(...args: any[]) {
      super(...args);
      delete this.id;
      delete this.version;
      delete this.createdAt;
      delete this.createdBy;
      delete this.updatedAt;
      delete this.updatedBy;
      delete this.authorization;
    }

    static from<E extends Entity, C>(entity: E, type: ClassConstructor<C>) {
      Object.getOwnPropertyNames(entity).forEach(key => {
        const field = key as keyof E;
        if (ENTITY_FIELDS.includes(key)) delete entity[field];
      });
      return entity as any as C;
    }
  };
}

interface UpdateEntity<T> {}
export function forUpdate<T extends ClassType>(BaseClass: T) {
  return class UpdateTrait extends BaseClass implements UpdateEntity<T> {
    id!: string;
    version!: number;
    companyId!: string;

    constructor(...args: any[]) {
      super(...args);
      delete this.authorization;
    }

    static from<E extends Entity, C>(entity: E, type: ClassConstructor<C>) {
      Object.getOwnPropertyNames(entity).forEach(key => {
        const field = key as keyof E;
        if (ENTITY_FIELDS.includes(key)) delete entity[field];
      });
      return entity as any as C;
    }
  };
}

interface DeleteEntity<T> {}
export function forDelete<T extends ClassType>(BaseClass: T) {
  return class DeleteTrait extends BaseClass implements DeleteEntity<T> {
    id!: string;
    version!: number;
    companyId!: string;

    constructor(...args: any[]) {
      super(...args);
      delete this.authorization;
    }

    static from<C>(entity: Entity, type: ClassConstructor<C>) {
      const item = {} as any;
      item.companyId = entity.companyId;
      item.version = entity.version;
      item.id = entity.id;
      if (entity instanceof StockLocationEntity) item.stockLocationId = entity.stockLocationId;
      return item;
    }
  };
}
